import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import subscriptionsDashboardService from '../../services/subscriptionsDashboardService'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  Cloud,
  RefreshCw,
  CreditCard,
  CalendarDays,
  XCircle
} from 'lucide-react'
// Consolidated CSS is imported globally'

const Subscriptions = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [dashboardData, setDashboardData] = useState(null)
  const [hubStats, setHubStats] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load dashboard data
  useEffect(() => {
    if (moduleView === 'overview') {
      loadDashboardData()
    } else if (moduleView === 'transactions' || moduleView === 'reports') {
      loadHubStats()
    }
  }, [moduleView])

  const loadHubStats = async () => {
    try {
      const response = await subscriptionsDashboardService.getDashboardStats()
      if (response.success) setHubStats(response.data)
    } catch (error) {
      console.error('Error loading subscription hub stats:', error)
    }
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await subscriptionsDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
        setHubStats(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading subscriptions dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency || 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount || 0)
    } catch {
      return `₹${Number(amount || 0).toLocaleString('en-IN')}`
    }
  }

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Subscriptions</h1>
          <p>Manage recurring subscriptions and billing cycles</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/subscriptions')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Subscription Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Cloud size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.activeSubscriptions?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.activeSubscriptions?.change >= 0 ? '+' : ''}{dashboardData?.activeSubscriptions?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.activeSubscriptions?.count || 0)}
              </div>
              <div className="metric-title">Active Subscriptions</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.monthlyCost?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.monthlyCost?.change >= 0 ? '+' : ''}{dashboardData?.monthlyCost?.change?.toFixed(1) || 0}%</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatCurrency(dashboardData?.monthlyCost?.amount || 0, dashboardData?.monthlyCost?.currency || 'INR')}
              </div>
              <div className="metric-title">Monthly committed (INR)</div>
              <div className="metric-subtitle">Active subscriptions</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="metric-trend negative">
                  <TrendingDown size={12} />
                  <span>Next {dashboardData?.expiringSoon?.days || 30}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.expiringSoon?.count || 0)}
              </div>
              <div className="metric-title">Expiring Soon</div>
              <div className="metric-subtitle">Requires attention</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <RefreshCw size={16} />
                </div>
                <div className="metric-trend neutral">
                  <CheckCircle size={12} />
                  <span>{dashboardData?.autoRenewalRate?.status || 'GOOD'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : `${dashboardData?.autoRenewalRate?.rate || 0}%`}
              </div>
              <div className="metric-title">Auto-Renewal Rate</div>
              <div className="metric-subtitle">Renewal efficiency</div>
            </div>

            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon"><DollarSign size={16} /></div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatCurrency(dashboardData?.annualCost?.amount || dashboardData?.kpis?.annualCommittedInr || 0)}
              </div>
              <div className="metric-title">Annual committed</div>
              <div className="metric-subtitle">INR equivalent</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon"><FileText size={16} /></div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatCurrency(dashboardData?.fyActualCost?.amount || dashboardData?.kpis?.fyActualCostInr || 0)}
              </div>
              <div className="metric-title">FY actual cost</div>
              <div className="metric-subtitle">Paid this financial year</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon"><AlertTriangle size={16} /></div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.kpis?.overduePayments || 0)}
              </div>
              <div className="metric-title">Overdue payments</div>
              <div className="metric-subtitle">RCM: {dashboardData?.kpis?.rcmApplicable || 0} · Missing invoices: {dashboardData?.kpis?.missingInvoices || 0}</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon"><Users size={16} /></div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.kpis?.unusedLicences || 0)}
              </div>
              <div className="metric-title">Unused licences</div>
              <div className="metric-subtitle">Rental returns due: {dashboardData?.kpis?.rentalReturnDue || 0}</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Subscription Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems && dashboardData.actionItems.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = {
                      renewal: AlertTriangle,
                      payment: CreditCard,
                      review: Cloud
                    }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon">
                          <Icon size={16} />
                        </div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Subscription Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity && dashboardData.teamActivity.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Cost by category (monthly INR)</h3>
                <p>Active subscriptions</p>
              </div>
              <div className="chart-content">
                <div className="revenue-chart-placeholder">
                  <div className="chart-bars">
                    {(dashboardData?.distribution?.byCategory || []).slice(0, 6).map((row) => {
                      const max = Math.max(...(dashboardData.distribution.byCategory.map((x) => x.amount) || [1]), 1)
                      return (
                        <div key={row.name} className="bar" title={`${row.name}: ${formatCurrency(row.amount)}`} style={{ height: `${Math.max(8, (row.amount / max) * 100)}%` }} />
                      )
                    })}
                    {!(dashboardData?.distribution?.byCategory || []).length && (
                      <div style={{ padding: 20, color: '#5B6478' }}>No subscription cost data yet</div>
                    )}
                  </div>
                  <div className="chart-labels">
                    {(dashboardData?.distribution?.byCategory || []).slice(0, 6).map((row) => (
                      <span key={row.name}>{row.name}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Subscription Status Distribution</h3>
                <p>Current portfolio</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {(dashboardData?.statusChart || []).slice(0, 6).map((seg) => (
                    <div key={seg.name} className="health-segment good">
                      <div className="segment-bar" style={{ width: `${Math.max(4, seg.percent)}%` }} />
                      <div className="segment-info">
                        <span className="segment-label">{seg.name}</span>
                        <span className="segment-count">{seg.count}</span>
                      </div>
                    </div>
                  ))}
                  {!(dashboardData?.statusChart || []).length && (
                    <div style={{ padding: 20, color: '#5B6478' }}>No status data yet</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="subscriptions-masters-content">
            <div className="subscriptions-masters-header">
              <h2>Subscription Masters</h2>
              <p>Category master is company-scoped. Defaults are created on first open; historical enum values remain valid on existing records.</p>
            </div>
            <div className="subscriptions-masters-grid">
              <div
                className="subscriptions-master-card"
                style={{ cursor: 'pointer' }}
                onClick={async () => {
                  try {
                    const subscriptionService = (await import('../../services/subscriptionService')).default
                    const res = await subscriptionService.getCategories?.() || await (await import('../../services/api')).default.get('/subscriptions/categories')
                    const data = res.data?.data || res.data || []
                    toast.success(`${Array.isArray(data) ? data.length : 0} categories available (admin can add/edit via API)`)
                  } catch (err) {
                    if (!err._errorHandled) toast.error('Failed to load categories')
                  }
                }}
              >
                <div className="subscriptions-master-icon">
                  <Target size={24} />
                </div>
                <div className="subscriptions-master-content">
                  <h3>Categories</h3>
                  <p>Add/edit, activate/deactivate, sort order, default billing model &amp; criticality</p>
                  <div className="subscriptions-master-count">Managed master</div>
                </div>
              </div>
              <div className="subscriptions-master-card">
                <div className="subscriptions-master-icon">
                  <CreditCard size={24} />
                </div>
                <div className="subscriptions-master-content">
                  <h3>Reminder settings</h3>
                  <p>Timezone, look-back hours, weekend policy (admin API `/subscriptions/settings`)</p>
                  <div className="subscriptions-master-count">Company settings</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="subscriptions-transactions-content">
            <div className="subscriptions-transactions-header">
              <h2>Subscription Transactions</h2>
              <p>Track and manage all subscription activities and renewals</p>
            </div>
            <div className="subscriptions-transactions-grid">
              <ModuleOverviewCard
                prefix="subscriptions-transaction"
                moduleName="Subscriptions"
                showArrow={false}
                onClick={() => navigate('/subscriptions?view=overview')}
              />
              <div
                className="subscriptions-transaction-card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions/list?filter=new')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/subscriptions/list?filter=new')}
                role="button"
                tabIndex={0}
              >
                <div className="subscriptions-transaction-icon">
                  <Cloud size={24} />
                </div>
                <div className="subscriptions-transaction-content">
                  <h3>New Subscriptions</h3>
                  <p>Recently added and activated subscriptions</p>
                  <div className="subscriptions-transaction-count">Create / list</div>
                </div>
              </div>
              <div
                className="subscriptions-transaction-card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions/renewals')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/subscriptions/renewals')}
                role="button"
                tabIndex={0}
              >
                <div className="subscriptions-transaction-icon">
                  <RefreshCw size={24} />
                </div>
                <div className="subscriptions-transaction-content">
                  <h3>Renewals</h3>
                  <p>Subscription renewals and extensions</p>
                  <div className="subscriptions-transaction-count">{hubStats?.renewalsSoon?.count ?? hubStats?.kpis?.upcomingRenewals30 ?? '—'} Due</div>
                </div>
              </div>
              <div
                className="subscriptions-transaction-card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions/payments')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/subscriptions/payments')}
                role="button"
                tabIndex={0}
              >
                <div className="subscriptions-transaction-icon">
                  <CreditCard size={24} />
                </div>
                <div className="subscriptions-transaction-content">
                  <h3>Payments</h3>
                  <p>Subscription payments and billing cycles</p>
                  <div className="subscriptions-transaction-count">{hubStats?.kpis?.overduePayments ?? 0} Overdue</div>
                </div>
              </div>
              <div
                className="subscriptions-transaction-card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/subscriptions/expirations')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/subscriptions/expirations')}
                role="button"
                tabIndex={0}
              >
                <div className="subscriptions-transaction-icon">
                  <CalendarDays size={24} />
                </div>
                <div className="subscriptions-transaction-content">
                  <h3>Expirations</h3>
                  <p>Upcoming subscription expirations</p>
                  <div className="subscriptions-transaction-count">{hubStats?.expiringSoon?.count ?? hubStats?.kpis?.upcomingExpirations30 ?? '—'} Soon</div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button type="button" className="btn-primary" onClick={() => navigate('/subscriptions/new')}>
                Create subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="subscriptions-reports-content">
            <div className="subscriptions-reports-header">
              <h2>Subscription Reports</h2>
              <p>Open a report — data is generated from live subscription records (export via browser or existing ERP export tools)</p>
            </div>
            <div className="subscriptions-reports-grid">
              {[
                { type: 'active-register', title: 'Active Subscription Register', desc: 'All active subscriptions', icon: Cloud },
                { type: 'upcoming-renewals', title: 'Upcoming Renewal Report', desc: 'Renewals in the next 30–90 days', icon: RefreshCw },
                { type: 'upcoming-expiry', title: 'Upcoming Expiry Report', desc: 'Expiries and notice deadlines', icon: CalendarDays },
                { type: 'payments', title: 'Payment Register', desc: 'All subscription payments', icon: CreditCard },
                { type: 'vendor-cost', title: 'Vendor-wise Cost', desc: 'Monthly committed cost by vendor', icon: BarChart3 },
                { type: 'department-cost', title: 'Department-wise Cost', desc: 'Cost allocation by department', icon: Briefcase },
                { type: 'project-cost', title: 'Project / Product Cost', desc: 'Cost by project or product', icon: Target },
                { type: 'foreign-currency', title: 'Foreign Currency', desc: 'Non-INR and foreign vendors', icon: DollarSign },
                { type: 'rcm', title: 'RCM Subscription Report', desc: 'Import of services / RCM', icon: FileText },
                { type: 'unused-licences', title: 'Unused Licence Report', desc: 'Purchased vs assigned seats', icon: Users },
                { type: 'missing-invoice', title: 'Missing Invoice Report', desc: 'Subscriptions without invoice docs', icon: AlertTriangle },
                { type: 'overdue-payments', title: 'Overdue Payment Report', desc: 'Due and overdue payments', icon: Clock },
                { type: 'rental-return', title: 'Rental Return Due', desc: 'Assets approaching return', icon: CalendarDays },
                { type: 'cost-increase', title: 'Cost Increase Report', desc: 'Proposed renewals above 10%', icon: TrendingUp },
                { type: 'cancelled-still-billed', title: 'Cancelled Still Billed', desc: 'Inactive services still charged', icon: XCircle },
                { type: 'monthly-annual-cost', title: 'Monthly & Annual Cost', desc: 'Committed cost summary', icon: DollarSign },
              ].map((report) => {
                const Icon = report.icon
                return (
                  <div
                    key={report.type}
                    className="subscriptions-report-card"
                    style={{ cursor: 'pointer' }}
                    onClick={async () => {
                      try {
                        const subscriptionService = (await import('../../services/subscriptionService')).default
                        const res = await subscriptionService.getReport(report.type, { days: 90 })
                        if (res.success) {
                          const rows = res.data?.rows || res.data?.distribution || res.data
                          console.info(`[subscriptions-report] ${report.type}`, rows)
                          toast.success(`${report.title}: ${Array.isArray(rows) ? rows.length : 'summary'} record(s) — see browser console / network response`)
                          if (report.type === 'upcoming-renewals') navigate('/subscriptions/renewals')
                          else if (report.type === 'upcoming-expiry' || report.type === 'rental-return') navigate('/subscriptions/expirations')
                          else if (report.type === 'payments' || report.type === 'overdue-payments') navigate('/subscriptions/payments')
                          else if (report.type === 'active-register') navigate('/subscriptions/list')
                        } else toast.error(res.message || 'Report failed')
                      } catch (err) {
                        if (!err._errorHandled) toast.error('Failed to load report')
                      }
                    }}
                  >
                    <div className="subscriptions-report-icon">
                      <Icon size={24} />
                    </div>
                    <div className="subscriptions-report-content">
                      <h3>{report.title}</h3>
                      <p>{report.desc}</p>
                      <div className="subscriptions-report-status">Live</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Subscriptions




